/* eslint-disable no-underscore-dangle */
import { memo } from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';

import { BASE_URL } from '@/shared/config/env';
import { joinBaseUrl } from '@/shared/utils/url';

import Spinner from '@/shared/components/spinner/Spinner';

import '@/shared/components/button/styles.scss';

import type { Ref } from 'react';
import type { ButtonProps } from '@/shared/components/button/types';

const Button = ({
	active,
	additionalSubTitle,
	avaName,
	avaOutside,
	btnType = 'submit',
	buttonRef,
	children,
	className,
	color,
	correctAnswer = null,
	description,
	disabled,
	disabledDropdownItem,
	download,
	href,
	iconClassName = 'icon',
	iconName,
	imgSrc,
	id,
	label,
	labelColor,
	link,
	loading,
	name = '',
	onClick,
	pressed,
	rounded,
	small,
	subTitle,
	symbol,
	tabIndex = -1,
	title,
	type = 'primary',
	style,
	testId,
	onPointerDown,
	onPointerUp,
}: ButtonProps) => {
	const hasBevel = ['primary', 'secondary'].includes(type) && !disabled;
	const bevelColor = color ?? (type === 'secondary' ? 'sand' : 'yellow');
	const hasControlledPressed = typeof pressed === 'boolean';
	const ariaPressed = hasControlledPressed ? pressed : undefined;
	const hasClassNamePressed = typeof className === 'string' && className.split(/\s+/).includes('pressed');

	const _className = classNames(
		hasBevel && 'btn-bevel',
		hasBevel && bevelColor && `btn-bevel--${bevelColor}`,
		type ? `btn-${type}` : null,
		{
			disabled,
			loading,
			small,
			rounded,
			active: active || loading,
			grey: color === 'grey',
			green: color === 'green',
			white: color === 'white',
			red: color === 'red',
			blue: color === 'blue',
			sea: color === 'sea',
			purple: color === 'purple',
			correct: correctAnswer,
			incorrect: correctAnswer === false,
			'disabled-dropdown-item': disabledDropdownItem,
		},
		hasBevel && !hasClassNamePressed && (pressed || active || loading) && 'pressed',
		className,
		description ? 'sub-desc' : null,
	);

	const handleClickButton = (e: any) => {
		if (disabled || loading || disabledDropdownItem) {
			return;
		}
		onClick && onClick(e);
	};
	const handlePointerDown = (e: any) => {
		if (disabled || loading || disabledDropdownItem) {
			return;
		}
		onPointerDown && onPointerDown(e);
	};
	const handlePointerUp = () => {
		if (disabled || loading || disabledDropdownItem) {
			return;
		}
		onPointerUp && onPointerUp();
	};

	const VisualWrapper = () => (
		<>
			{['primary', 'secondary'].indexOf(type) >= 0 ? (
				<div className="visual">
					<Content />
				</div>
			) : (
				<Content />
			)}
		</>
	);

	const Content = () => (
		<>
			{avaName && !symbol && (
				<div className="avatar">
					<img
						src={avaOutside ? avaName : joinBaseUrl(BASE_URL, avaName)}
						alt="subjectLogo"
						width="24px"
						height="24px"
					/>
				</div>
			)}
			{iconName && !symbol && !avaName && !loading && <span className={classNames(iconClassName, iconName)} />}
			{imgSrc && <img className="image" src={imgSrc} alt="img" />}
			{title && !subTitle && !description && <span className="text">{title}</span>}
			{title && description && (
				<div className="desc-block">
					<span className="text">{title}</span>
					<span className="text desc">{description}</span>
				</div>
			)}
			{title && subTitle && !description && (
				<div className="desc-block">
					<span className="text">{title}</span>
					<span className="text sub-text">{subTitle}</span>
					{additionalSubTitle && <span className="text sub-text">{additionalSubTitle}</span>}
				</div>
			)}
			{loading && <Spinner />}
			{symbol && <span className="symbol">{symbol}</span>}
			{label && <span className={classNames('label-wrapper', labelColor)}>{label}</span>}
			{children}
		</>
	);

	const ButtonLink = () =>
		href ? (
			<a
				id={id}
				href={href}
				className={_className}
				rel="noopener noreferrer"
				download={download}
				target="_blank"
				data-testid={testId}
				aria-pressed={ariaPressed}
				ref={buttonRef as Ref<HTMLAnchorElement>}
			>
				<VisualWrapper />
			</a>
		) : link ? (
			<Link
				id={id}
				tabIndex={tabIndex}
				to={link}
				className={_className}
				onClick={handleClickButton}
				data-testid={testId}
				aria-pressed={ariaPressed}
				style={style}
				ref={buttonRef as Ref<HTMLAnchorElement>}
			>
				<VisualWrapper />
			</Link>
		) : (
			<button
				id={id}
				className={_className}
				onClick={handleClickButton}
				onPointerDown={handlePointerDown}
				onPointerUp={handlePointerUp}
				onPointerLeave={handlePointerUp}
				tabIndex={tabIndex}
				type={btnType}
				name={name}
				data-testid={testId}
				aria-pressed={ariaPressed}
				style={style}
				ref={buttonRef as Ref<HTMLButtonElement>}
			>
				<VisualWrapper />
			</button>
		);

	return <ButtonLink />;
};

export default memo(Button);
